import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';

import { SwitchField } from '@ovComponents/3-pattern/switchField/switchField';
import { Box, Button, IconButton, Typography, useTheme } from 'hephaestus/web';
import { FilterList as Filter } from 'hephaestus/webIcons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertBannerStatus } from '../types';

export default function alertBannerFilterModal({
  initialStatuses,
  filterExists,
  onApply,
}: {
  initialStatuses: AlertBannerStatus[];
  onApply: (status: AlertBannerStatus[]) => void;
  filterExists: boolean;
}) {
  const theme = useTheme();
  const { t } = useTranslation('alertBanners');
  const [open, setOpen] = useState(false);
  const [statuses, setStatuses] =
    useState<AlertBannerStatus[]>(initialStatuses);

  const handleCancel = () => {
    setOpen(false);
    setStatuses(initialStatuses);
  };

  return (
    <>
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <IconButton aria-label='filter' onClick={() => setOpen(true)}>
          <Filter fontSize='inherit' />
        </IconButton>
        {filterExists && (
          <Box
            sx={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              height: '8px',
              width: '8px',
              borderRadius: '50%',
              backgroundColor: theme.color.negative,
            }}
          />
        )}
      </Box>
      <Dialog
        open={open}
        fullWidth
        maxWidth='sm'
        sx={{
          '.MuiDialog-container': {
            alignItems: 'flex-start',
            paddingTop: 2,
          },
        }}
      >
        <DialogTitle onClose={handleCancel}>
          <Typography variant='headingMedium'>
            {t('filterModal.title')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              py: 1,
            }}
          >
            <Typography variant='bodyLarge' color='textSecondary'>
              {t('filterModal.status')}
            </Typography>
            {Object.values(AlertBannerStatus).map((status) => (
              <SwitchField
                key={status}
                label={t('filterModal.showStatus', {
                  status: t(`table.status.states.${status}`),
                })}
                fullWidth
                labelPosition='left'
                checked={statuses.includes(status)}
                onChange={(checked) =>
                  setStatuses((prev) =>
                    checked
                      ? [...prev, status]
                      : prev.filter((x) => x !== status),
                  )
                }
                sx={{
                  '& .MuiTypography-root': {
                    color: theme.color.onSurface,
                  },
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogFooter>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              p: 1,
            }}
          >
            <Button
              onClick={handleCancel}
              variant='contained'
              color='secondary'
            >
              {t('filterModal.cancel')}
            </Button>
            <Button
              onClick={() => {
                onApply(statuses);
                setOpen(false);
              }}
            >
              {t('filterModal.apply')}
            </Button>
          </Box>
        </DialogFooter>
      </Dialog>
    </>
  );
}
